import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Check, Copy, Download, MousePointerClick, RefreshCw, Tag } from "lucide-react";
import { Navbar, Footer } from "@/components/landing";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { CHROME_WEB_STORE_URL, EXTENSION_ZIP_PATH } from "@/config/extension";
import { trackEvent } from "@/lib/umami";

const FEATURES = [
  { key: "fill", icon: MousePointerClick },
  { key: "label", icon: Tag },
  { key: "reuse", icon: RefreshCw },
] as const;

const STEPS = ["download", "open", "developer", "load", "connect"] as const;

/** Public install page for the browser extension (until the store listing exists). */
export function ExtensionPage() {
  const { t } = useTranslation("landing");
  const { copy, copied } = useCopyToClipboard();

  return (
    <div className="relative min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pb-14">
          <div aria-hidden="true" className="absolute inset-0 bg-mesh-low clip-skew md:bg-mesh" />
          <div className="relative mx-auto max-w-[1200px] px-5 pt-32 md:px-10 md:pt-40">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[13px] font-semibold text-white">
              <span className="size-1.5 rounded-full bg-[#7cf5d0]" />
              {t("extension.badge")}
            </span>
            <h1 className="mt-6 max-w-[760px] text-[40px] leading-[1.04] font-bold tracking-[-0.045em] text-balance text-white sm:text-5xl lg:text-6xl">
              {t("extension.title")}
            </h1>
            <p className="mt-5 max-w-[560px] text-lg leading-relaxed text-[#d5deea]">
              {t("extension.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="shadow-primary">
                {CHROME_WEB_STORE_URL ? (
                  <a
                    href={CHROME_WEB_STORE_URL}
                    target="_blank"
                    rel="noopener"
                    onClick={() =>
                      trackEvent("extension_install_click", { channel: "chrome_web_store" })
                    }
                  >
                    {t("extension.addToChrome")}
                  </a>
                ) : (
                  <a
                    href={EXTENSION_ZIP_PATH}
                    download
                    onClick={() => trackEvent("extension_install_click", { channel: "zip" })}
                  >
                    <Download />
                    {t("extension.download")}
                  </a>
                )}
              </Button>
              <span className="text-sm text-[#d5deea]">{t("extension.browsers")}</span>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-[1200px] gap-4 px-5 pt-10 md:grid-cols-3 md:px-10">
          {FEATURES.map(({ key, icon: Icon }) => (
            <div key={key} className="rounded-2xl border bg-card p-6 shadow-card">
              <div className="flex size-9 items-center justify-center rounded-xl bg-accent text-primary">
                <Icon className="size-4" />
              </div>
              <h2 className="mt-4 font-semibold">{t(`extension.features.${key}.title`)}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t(`extension.features.${key}.body`)}
              </p>
            </div>
          ))}
        </section>

        {CHROME_WEB_STORE_URL ? null : (
          <section className="mx-auto max-w-[1200px] px-5 pt-16 md:px-10" id="install">
            <h2 className="text-3xl font-bold tracking-[-0.03em]">{t("extension.installTitle")}</h2>
            <p className="mt-2 max-w-[620px] text-muted-foreground">{t("extension.installBody")}</p>
            <ol className="mt-8 grid gap-3">
              {STEPS.map((step, i) => (
                <li key={step} className="flex gap-4 rounded-2xl border bg-card p-5 shadow-card">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent font-mono text-sm font-semibold text-primary">
                    {i + 1}
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p className="font-medium">{t(`extension.steps.${step}.title`)}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t(`extension.steps.${step}.body`)}
                    </p>
                    {step === "open" ? (
                      // chrome:// links cannot be opened from a web page, so offer a copy.
                      <button
                        type="button"
                        onClick={() => void copy("chrome://extensions")}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border bg-muted px-3 py-1.5 font-mono text-sm hover:border-primary"
                      >
                        chrome://extensions
                        {copied ? (
                          <Check className="size-3.5 text-protect" />
                        ) : (
                          <Copy className="size-3.5 text-muted-foreground" />
                        )}
                      </button>
                    ) : null}
                    {step === "connect" ? (
                      <Button asChild variant="outline" size="sm" className="mt-3">
                        <Link to="/app/settings" search={{ tab: "extension" }}>
                          {t("extension.openSettings")}
                        </Link>
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default ExtensionPage;
